import { useEffect, useRef, useState } from 'react';

export const useScrollReveal = (options = {}) => {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(element);
        }
      },
      {
        threshold: options.threshold || 0.12,
        rootMargin: options.rootMargin || '0px 0px -40px 0px',
      }
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, [options.threshold, options.rootMargin]);

  return [ref, isVisible];
};

export const RevealSection = ({ children, className = "", delay = 0 }) => {
  const [ref, isVisible] = useScrollReveal();

  return (
    <div
      ref={ref}
      className={`transition-all duration-[900ms] ease-out ${className}`}
      style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translateY(0)' : 'translateY(24px)',
        transitionDelay: `${delay}ms`,
      }}
    >
      {children}
    </div>
  );
};

export const RevealText = ({ children, className = "", delay = 0 }) => {
  const [ref, isVisible] = useScrollReveal();

  return (
    <div
      ref={ref}
      className={`transition-all duration-[800ms] ease-out ${className}`}
      style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translateY(0)' : 'translateY(16px)',
        transitionDelay: `${delay}ms`,
      }}
    >
      {children}
    </div>
  );
};

export const RevealImage = ({ src, alt, className = "" }) => {
  const [ref, isVisible] = useScrollReveal({ threshold: 0.08 });

  return (
    <div
      ref={ref}
      className={`overflow-hidden transition-all duration-[1200ms] ease-out ${className}`}
      style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'scale(1)' : 'scale(1.02)',
      }}
    >
      <img
        src={src}
        alt={alt}
        className="w-full h-full object-cover"
        loading="lazy"
      />
    </div>
  );
};

export const PullQuote = ({ children, className = "" }) => {
  const [ref, isVisible] = useScrollReveal();

  return (
    <blockquote
      ref={ref}
      className={`relative pl-6 border-l-2 border-copper/40 transition-all duration-[900ms] ease-out ${className}`}
      style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translateX(0)' : 'translateX(-12px)',
      }}
    >
      <p className="font-display text-xl md:text-2xl text-vine-primary/70 font-light leading-relaxed italic">
        {children}
      </p>
    </blockquote>
  );
};

export const PullQuoteLight = ({ children, className = "" }) => {
  const [ref, isVisible] = useScrollReveal();

  return (
    <blockquote
      ref={ref}
      className={`relative pl-6 border-l-2 border-copper/30 transition-all duration-[900ms] ease-out ${className}`}
      style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translateX(0)' : 'translateX(-12px)',
      }}
    >
      <p className="font-display text-xl md:text-2xl text-vine-neutral/60 font-light leading-relaxed italic">
        {children}
      </p>
    </blockquote>
  );
};
